import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { VerificationQuickAdd, type VerificationFormProps } from "@/components/VerificationQuickAdd"
import { PhotoManager, type PhotoManagerProps } from "@/components/PhotoManager"

type DestinationDetail = {
  id: number
  name_en: string | null
  name_de: string | null
  slug_en: string | null
  slug_de: string | null
  kind: string
  status: string
  parent_name: string | null
  latitude: number | null
  longitude: number | null
  updated_at: string | null
}

export default function AdminDestinationsShow({ destination, verification_form, photo_manager }: { destination: DestinationDetail; verification_form: VerificationFormProps; photo_manager: PhotoManagerProps }) {
  const title = destination.name_en ?? "Destination"
  const description = `Admin details and verification history for ${title}.`
  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{destination.name_en ?? destination.name_de ?? `Destination #${destination.id}`}</h1>
              <p className="mt-1">
                {titleize(destination.kind)}
                {destination.parent_name ? ` in ${destination.parent_name}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={destination.status === "published" ? "accent" : "muted"}>
                {titleize(destination.status)}
              </Badge>
              <Button asChild variant="secondary">
                <Link href={`/admin/destinations/${destination.id}/edit`}>Edit</Link>
              </Button>
            </div>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
          <DetailRow label="English slug" value={destination.slug_en ?? "-"} />
          <DetailRow label="German slug" value={destination.slug_de ?? "-"} />
          <DetailRow label="Center" value={`${destination.latitude}, ${destination.longitude}`} />
          <DetailRow label="Updated" value={destination.updated_at ? formatDateTime(destination.updated_at) : "-"} />
        </dl>
        <PhotoManager photoManager={photo_manager} />
        <VerificationQuickAdd verificationForm={verification_form} />
      </AdminShell>
    </>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink-body">{value}</dd>
    </>
  )
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
